import React, { useState, useEffect } from 'react';
import { IconValidateNew, IconClose } from 'components/Icons';
import { ChartOfAccountsSelect } from 'presentation/pages/bookkeeper/components/BookkeeperAccrualsSettingsForm';
import CustomModal from 'components/CustomModal/CustomModal';
import { KSButton } from 'presentation/components/KSQ2/Common';
import './RecategorizeDialog.scss';

interface RecategorizeDialogProps {
  isOpen: boolean;
  isPending: boolean;
  onClose: () => void;
  onSubmit: (form: { comment: string; account: string }) => void;
}

/**
 * Modal dialog for recategorizing a transaction
 * Allows selecting a new GL account and adding a comment
 */
export const RecategorizeDialog: React.FC<RecategorizeDialogProps> = ({ isOpen, isPending, onClose, onSubmit }) => {
  const [comment, setComment] = useState<string>('');
  const [account, setAccount] = useState<string>('');

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setComment('');
        setAccount('');
      }, 300);
    }
  }, [isOpen]);

  const handleCOASelect = (value: { value: string; label: string }) => {
    setAccount(value.value);
  };

  const handleSubmit = () => {
    onSubmit({ comment, account });
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="recategorize-modal"
      shouldCloseOnEsc={!isPending}
      shouldCloseOnOverlayClick={!isPending}
    >
      <div className="recategorize-modal__content">
        <h2 className="recategorize-modal__title">Re-Categorize Transaction</h2>

        <div className="recategorize-modal__form">
          <div className="recategorize-modal__field">
            <label className="recategorize-modal__label">Select a new GL category</label>
            <ChartOfAccountsSelect selected={account} onChange={handleCOASelect} />
          </div>

          <div className="recategorize-modal__field">
            <label className="recategorize-modal__label">Reason for change</label>
            <textarea
              className="recategorize-modal__textarea"
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Specify the reason for changing the category."
              disabled={isPending}
              rows={4}
            />
          </div>
        </div>

        <div className="recategorize-modal__actions">
          <KSButton variant="secondary" icon={<IconClose />} label="Cancel" onClick={onClose} disabled={isPending} />
          <KSButton
            variant="primary"
            icon={isPending ? <i className="fa fa-circle-o-notch fa-spin" /> : <IconValidateNew />}
            label={isPending ? 'Updating...' : 'Re-Categorize'}
            onClick={handleSubmit}
            disabled={!account || isPending}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default RecategorizeDialog;
